/**
 * ChangeOrderSignView.js: IDI-452 v2 (1)/(2b). What a customer reads when
 * signing a CHANGE ORDER. Rendered from the server's IDI-302 projection
 * (`quote.kind === 'change_order'`), never as a quote.
 *
 *   added   -> full price
 *   changed -> what changed, and its +/- amount (and "2,500 -> 1,350 sq ft")
 *   removed -> "Removed", with a negative amount (U-08)
 *   Net change, tax at the job's signed rate (U-09/U-27), and
 *   "Change order total" = net + tax.
 *
 * Tax unknown (C-02): lines and the net are shown, plus a note to contact the
 * contractor; the parent disables signing (`doc.signable === false`).
 * Original -> revised totals are internal only (D-03) and never shown.
 */
import React from 'react';
import { formatCurrency } from '../../utils/formatCurrency';
import { sanitizeTcHtml } from '../../utils/sanitizeTcHtml';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';

const qty = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString() : '');

// IDI-639: shared with Add to Job's preview (ChangeOrderDetail), so both read
// "Needs price" for an unpriced added line. IDI-726: the optional props
// default to today's row, so Add to Job's preview is unchanged.
export function LineRow({ l, showQty = true, showAmount = true, whereMode = 'full' }) {
  const where = whereMode === 'sub' ? (l.subArea || '') : [l.area, l.subArea].filter(Boolean).join(' · ');
  const unit = l.unit ? ` ${l.unit.replace('_', ' ')}` : '';
  let detail = '';
  if (l.kind === 'changed' && l.previousQuantity != null) detail = `${qty(l.previousQuantity)} → ${qty(l.quantity)}${unit}`;
  else if (l.kind !== 'removed' && l.quantity) detail = `${qty(l.quantity)}${unit}`;
  const tag = { added: 'Added', changed: 'Changed', removed: 'Removed' }[l.kind] || '';
  return (
    <TableRow>
      <TableCell>
        <div className="text-sm">{l.item || 'Item'}</div>
        {where && <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{where}</div>}
      </TableCell>
      <TableCell>{tag}</TableCell>
      {showQty && <TableCell align="right" className="tabular-nums">{detail}</TableCell>}
      {/* IDI-137 D6: an added line coprice can't price reads "Needs price", never $0.00. */}
      {showAmount && (
        <TableCell align="right" className="tabular-nums">{l.needsPrice ? 'Needs price' : formatCurrency(l.amountCents)}</TableCell>
      )}
    </TableRow>
  );
}

// IDI-726 D3: the quote's display options, as on the quote's document.
// Absent options are on.
const optOn = (opts, key) => !opts || opts[key] !== false;

// Group by area: areas in first-seen order, each area's lines in their order;
// lines with no area keep their place, under no heading.
function groupByArea(lines) {
  const groups = [];
  const byArea = new Map();
  lines.forEach((l, i) => {
    const area = l.area || '';
    if (!byArea.has(area)) {
      const g = { area, items: [] };
      byArea.set(area, g);
      groups.push(g);
    }
    byArea.get(area).items.push({ l, i });
  });
  return groups;
}

// IDI-726 D4: `options` and `notes` let the Review & Send preview follow the
// rail's unsaved toggles and notes; the signing page passes neither and reads
// the document as sent.
export default function ChangeOrderSignView({ doc, options, notes }) {
  if (!doc) return null;
  const lines = Array.isArray(doc.lines) ? doc.lines : [];
  const tax = doc.tax || {};
  const opts = options || doc.displayOptions || null;
  const grouped = optOn(opts, 'groupByArea');
  const showQty = optOn(opts, 'showSqft');
  const showAmount = optOn(opts, 'lineItemPricing');
  const showTax = optOn(opts, 'showTax');
  const noteText = optOn(opts, 'notes') ? String((notes !== undefined ? notes : doc.notes) || '').trim() : '';
  const colCount = 2 + (showQty ? 1 : 0) + (showAmount ? 1 : 0);
  const rowProps = { showQty, showAmount };
  return (
    <div className="p-6 space-y-4" data-testid="co-sign-view">
      <div>
        <h2 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          {doc.title || `Change Order ${doc.coNumber || ''}`.trim()}
        </h2>
        <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          {[doc.coNumber, doc.jobNumber && `Job ${doc.jobNumber}`].filter(Boolean).join(' · ')}
        </div>
      </div>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>Item</TableHeader>
            <TableHeader>Change</TableHeader>
            {showQty && <TableHeader align="right">Quantity</TableHeader>}
            {showAmount && <TableHeader align="right">Amount</TableHeader>}
          </TableRow>
        </TableHead>
        <TableBody>
          {grouped
            ? groupByArea(lines).map((g) => (
              <React.Fragment key={`area-${g.area}`}>
                {g.area && (
                  <TableRow>
                    <TableCell colSpan={colCount} className="font-semibold" data-testid="co-area-heading">{g.area}</TableCell>
                  </TableRow>
                )}
                {g.items.map(({ l, i }) => <LineRow key={`${l.kind}-${i}`} l={l} {...rowProps} whereMode="sub" />)}
              </React.Fragment>
            ))
            : lines.map((l, i) => <LineRow key={`${l.kind}-${i}`} l={l} {...rowProps} />)}
        </TableBody>
      </Table>
      <dl className="ml-auto max-w-xs space-y-1 text-sm">
        <div className="flex justify-between">
          <dt style={{ color: 'var(--color-text-muted)' }}>Net change</dt>
          <dd className="tabular-nums" data-testid="co-net">{formatCurrency(doc.netChangeCents)}</dd>
        </div>
        {tax.known ? (showTax && (
          <div className="flex justify-between">
            <dt style={{ color: 'var(--color-text-muted)' }}>{tax.caption || 'Tax'}</dt>
            <dd className="tabular-nums" data-testid="co-tax">{formatCurrency(tax.cents)}</dd>
          </div>
        )) : (
          <div className="text-xs" style={{ color: 'var(--color-text-muted)' }} data-testid="co-tax-unknown">
            Tax couldn&apos;t be determined for this change order. Please contact your contractor before signing.
          </div>
        )}
        {tax.known && (
          <div className="flex justify-between pt-1 border-t font-semibold" style={{ borderColor: 'var(--color-border)' }}>
            <dt style={{ color: 'var(--color-text-primary)' }}>Change order total</dt>
            <dd className="tabular-nums" data-testid="co-total">{formatCurrency(doc.totalCents)}</dd>
          </div>
        )}
      </dl>
      {/* IDI-726 D3/D6: the change order's Notes, after the totals, before the terms. */}
      {noteText && (
        <div data-testid="co-notes">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Notes</h3>
          <div className="text-sm whitespace-pre-wrap" style={{ color: 'var(--color-text-primary)' }}>{noteText}</div>
        </div>
      )}
      {/* IDI-137 D5: terms are rich-text HTML (or plain text); cleaned as on quotes.
          Plain text keeps its line breaks (pre-wrap only when there are no tags). */}
      {doc.terms && (
        <div
          className={`text-xs${/<[a-z][\s\S]*>/i.test(doc.terms) ? '' : ' whitespace-pre-wrap'}`}
          style={{ color: 'var(--color-text-muted)' }}
          data-testid="co-terms"
          dangerouslySetInnerHTML={{ __html: sanitizeTcHtml(doc.terms) }}
        />
      )}
    </div>
  );
}
